import React, { useMemo } from 'react';
import { Form } from 'react-final-form';
import { Button, ButtonSize, Col, Row } from '@estimateone/frontend-components';
import arrayMutators from 'final-form-arrays';
import setFieldTouched from 'final-form-set-field-touched';
import SubbieDetails from './SubbieDetails';
import GlobalErrorList from '../../../../shared/FinalForm/GlobalErrorList';
import { LegacyFileManagerParams } from '@shared/FileUploader/types';
import { ItpButtonStyleWrapper, ItpButtonType } from '@shared/Itp/ItpStyleWrapper';
import { QuoteFormQuoteSectionWithoutExtraFiles } from '@shared/Quotes/Form/QuoteFormQuoteSection';
import { focusOnQuoteErrorsDecorator } from '@shared/Quotes/Form/decorators';
import {
  QuoteFormWihoutExtraFileFieldValues,
  QuoteFormWithoutExtraFileFieldErrors,
} from '@shared/Quotes/Form/types';
import { validateQuoteFormCommonFields } from '@shared/Quotes/Form/validators';
import { useItpSubbieContext } from '../../../context';
import { Action, AnalyticsEndpoint, Event, useAnalytics } from '../../../../hooks/Analytics';
import { useCreateOrReviseQuote } from './hooks';
import { UploadParentType } from '@ascension/enums';
import {
  QuoteSubbieAddFormValues,
  SubbieAddQuoteFormErrors,
} from '@subbie/features/QuotesFeature/AddQuoteForm/types';
import styles from './styles.scss';

const FORM_ID = 'itp-subbie-create-quote-form';

const validate = (values: QuoteSubbieAddFormValues): SubbieAddQuoteFormErrors => {
  const commonQuoteErrors = validateQuoteFormCommonFields(values);

  return { ...commonQuoteErrors };
};

export const ItpCreateQuoteForm = ({
  fileManagerEndpoint,
  fileManagerKey,
  fileManagerLocale,
}: LegacyFileManagerParams) => {
  const [{ account, user, stageId, rfqId, openSignupModal }] = useItpSubbieContext();

  const focusOnErrors = useMemo(
    () =>
      focusOnQuoteErrorsDecorator<
        QuoteFormWihoutExtraFileFieldValues,
        QuoteFormWithoutExtraFileFieldErrors
      >(FORM_ID),
    [],
  );

  const { submit: createOrReviseQuotes } = useCreateOrReviseQuote(rfqId, openSignupModal);

  const { addEvent } = useAnalytics(Event.INTERACT, AnalyticsEndpoint.Itp);

  const handleSubmitClicked = () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    addEvent({ action: Action.ITP_QUOTE_SUBMIT_CLICKED });
  };

  return (
    <>
      {user && <SubbieDetails account={account} user={user} />}

      <Form
        onSubmit={createOrReviseQuotes}
        decorators={[focusOnErrors]}
        validate={(values): QuoteFormWithoutExtraFileFieldErrors => validate(values)}
        mutators={{
          ...arrayMutators,
          setFieldTouched,
        }}
        keepDirtyOnReinitialize
      >
        {({ handleSubmit, submitting, submitError }) => (
          <form id={FORM_ID} onSubmit={handleSubmit}>
            <Row>
              <Col span={12}>{submitError && <GlobalErrorList errors={submitError} />}</Col>
            </Row>
            <QuoteFormQuoteSectionWithoutExtraFiles
              fileManagerEndpoint={fileManagerEndpoint}
              fileManagerKey={fileManagerKey}
              fileManagerLocale={fileManagerLocale}
              parentId={stageId}
              parentType={UploadParentType.STAGE_QUOTE}
            />
            <hr className={styles.itpFormHr} />
            <div className={styles.itpFormSubmitBtnContainer}>
              <ItpButtonStyleWrapper buttonType={ItpButtonType.Primary}>
                <Button
                  onClick={handleSubmitClicked}
                  size={ButtonSize.Large}
                  fullWidth
                  isDisabled={submitting}
                >
                  Submit
                </Button>
              </ItpButtonStyleWrapper>
            </div>
          </form>
        )}
      </Form>
    </>
  );
};
